import React, { useState, useRef, useEffect } from 'react';

const countries = [
    { code: '+1', name: 'United States', flag: '🇺🇸' },
    { code: '+1', name: 'Canada', flag: '🇨🇦' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+39', name: 'Italy', flag: '🇮🇹' },
    { code: '+34', name: 'Spain', flag: '🇪🇸' },
    { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
    { code: '+32', name: 'Belgium', flag: '🇧🇪' },
    { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', name: 'Austria', flag: '🇦🇹' },
    { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    { code: '+46', name: 'Sweden', flag: '🇸🇪' },
    { code: '+47', name: 'Norway', flag: '🇳🇴' },
    { code: '+358', name: 'Finland', flag: '🇫🇮' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹' },
    { code: '+30', name: 'Greece', flag: '🇬🇷' },
    { code: '+48', name: 'Poland', flag: '🇵🇱' },
    { code: '+420', name: 'Czech Republic', flag: '🇨🇿' },
    { code: '+36', name: 'Hungary', flag: '🇭🇺' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+65', name: 'Singapore', flag: '🇸🇬' },
    { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
    { code: '+853', name: 'Macau', flag: '🇲🇴' },
    { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
    { code: '+66', name: 'Thailand', flag: '🇹🇭' },
    { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
    { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', name: 'Philippines', flag: '🇵🇭' },
    { code: '+84', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' },
    { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
    { code: '+55', name: 'Brazil', flag: '🇧🇷' },
    { code: '+52', name: 'Mexico', flag: '🇲🇽' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: '+51', name: 'Peru', flag: '🇵🇪' },
    { code: '+27', name: 'South Africa', flag: '🇿🇦' },
    { code: '+20', name: 'Egypt', flag: '🇪🇬' },
    { code: '+971', name: 'UAE', flag: '🇦🇪' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+972', name: 'Israel', flag: '🇮🇱' },
    { code: '+90', name: 'Turkey', flag: '🇹🇷' },
    { code: '+7', name: 'Russia', flag: '🇷🇺' },
];

export default function CountryCodeDropdown({ value, onChange, placeholder = "+1" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState(value || '');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownSearchRef = useRef(null);

    // Find selected country
    const selectedCountry = countries.find(country => country.code === value);

    // Update input value when prop value changes
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Filter countries based on search term
    const filteredCountries = countries.filter(country =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.code.includes(searchTerm)
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus dropdown search input when dropdown opens
    useEffect(() => {
        if (isOpen && dropdownSearchRef.current) {
            dropdownSearchRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (country) => {
        setInputValue(country.code);
        onChange(country.code);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange(newValue);
    };

    const handleInputFocus = () => {
        // Don't open dropdown when focusing on input for typing
    };

    const handleDropdownToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        } else if (e.key === 'ArrowDown' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
        }
    };

    const handleDropdownKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            inputRef.current?.focus();
        }
    };

    return (
        <div className="country-code-dropdown" ref={dropdownRef}>
            <div className="country-code-input-container">
                {selectedCountry && (
                    <span className="country-flag">{selectedCountry.flag}</span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    className="country-code-input-field"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    className="dropdown-toggle-button"
                    onClick={handleDropdownToggle}
                    tabIndex={-1}
                >
                    <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
                </button>
            </div>

            {isOpen && (
                <div className="country-dropdown-list">
                    <input
                        ref={dropdownSearchRef}
                        type="text"
                        className="country-dropdown-search"
                        placeholder="Search countries..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleDropdownKeyDown}
                    />
                    {filteredCountries.length > 0 ? (
                        filteredCountries.map((country, index) => (
                            <div
                                key={`${country.code}-${country.name}-${index}`}
                                className="country-dropdown-item"
                                onClick={() => handleSelect(country)}
                            >
                                <span className="country-flag">{country.flag}</span>
                                <span className="country-name">{country.name}</span>
                                <span className="country-code-display">{country.code}</span>
                            </div>
                        ))
                    ) : (
                        <div className="country-dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                            No countries found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
} 